import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { T } from '@design/index';
import ProviderReviews from '../../components/ProviderReviews';
import { apiFileUrl } from '../../../../api/http';
import { patientApi } from '../../api';
import { useProviderSlots, preferredTimeGrid, formatSlotTime } from './useProviderSlots';
import type { MarketplaceProvider, ProviderCertificate, ProviderPricing } from '../../types';
import {
  AboutCard, AccreditationsCard, ClinicsCard, FaqCard, ProvidedByFooter, RegistrationCard, ServicesCard,
} from './DoctorCredentials';

// ─── Inline SVG Icons ────────────────────────────────────────────────────────

function IconBack({ color }: { color: string }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function IconStethoscope({ color }: { color: string }) {
  return (
    <svg width={40} height={40} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
      <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" />
      <circle cx="20" cy="10" r="2" />
    </svg>
  );
}

function IconStar({ filled, color }: { filled: boolean; color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill={filled ? color : 'none'} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function IconVerified({ color }: { color: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill={color} stroke="none">
      <path d="M12 2l2.5 4.5L19 7.5l-3.5 3.5.8 5L12 14l-4.3 2 .8-5L5 7.5l4.5-1L12 2z" />
    </svg>
  );
}

function IconLocation({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconPhone({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6.06 6.06l1.07-.87a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.94 16.92z" />
    </svg>
  );
}

function IconMail({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function IconClock({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function IconShield({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" />
    </svg>
  );
}

function IconCertificate({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M16 13H8M16 17H8M10 9H8" />
    </svg>
  );
}

function IconVideo({ color }: { color: string }) {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 7l-7 5 7 5V7z" />
      <rect x="1" y="5" width="15" height="14" rx="2" />
    </svg>
  );
}

function IconBuilding({ color }: { color: string }) {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18" />
      <path d="M5 21V7l7-4 7 4v14" />
      <rect x="9" y="10" width="6" height="6" />
    </svg>
  );
}

// ─── Loading Spinner ──────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div
        style={{
          width: 40,
          height: 40,
          border: `3px solid ${T.border}`,
          borderTop: `3px solid ${T.teal}`,
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────

function SectionHeader({ title }: { title: string }) {
  return (
    <div style={{ padding: '20px 16px 10px' }}>
      <h2 style={{ fontFamily: T.font, fontSize: 14, fontWeight: 800, color: T.text, margin: 0, letterSpacing: '-0.01em' }}>
        {title}
      </h2>
      <div style={{ width: 28, height: 2.5, background: T.teal, borderRadius: 2, marginTop: 5 }} />
    </div>
  );
}

function getDateLabel(offset: number) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  if (offset === 0) return 'Today';
  if (offset === 1) return 'Tomorrow';
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** Chip style used inside the navy hero. */
const heroChip: React.CSSProperties = {
  fontFamily: T.font, fontSize: 10.5, fontWeight: 700, color: 'rgba(255,255,255,0.85)',
  background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: 20, padding: '3px 9px',
};

function getDateStr(offset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().split('T')[0];
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DoctorProfile() {
  const { doctorId } = useParams<{ doctorId: string }>();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState<MarketplaceProvider | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  /** Set only when the chosen time is one the doctor actually published. */
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  /** A photo that will not load falls back to the icon, not to an empty box. */
  const [photoBroken, setPhotoBroken] = useState(false);
  /** Saved doctors — `GET /favorites/ids`, the same list the directory writes. */
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Real published openings; `hasSchedule: false` means there is no schedule
  // to read and the chips below are a stated preference, labelled as such.
  const slots = useProviderSlots(doctorId, 14);

  useEffect(() => {
    if (!doctorId) return;
    setLoading(true);
    patientApi
      .getMarketplaceProvider(doctorId)
      .then((res) => {
        setDoctor(res);
        setError('');
      })
      .catch(() => {
        setError('Unable to load doctor profile.');
      })
      .finally(() => setLoading(false));
  }, [doctorId]);

  // Whether this doctor is already saved. The profile carried a heart with
  // nothing behind it; this reads the patient's real list.
  const providerId = doctor?.id;
  useEffect(() => {
    if (!providerId) return;
    let alive = true;
    patientApi.getFavoriteIds()
      .then((ids) => { if (alive) setSaved((ids ?? []).includes(providerId)); })
      .catch(() => { if (alive) setSaved(false); });
    return () => { alive = false; };
  }, [providerId]);

  const toggleSaved = async () => {
    if (!providerId) return;
    const wasSaved = saved;
    setSaved(!wasSaved);
    setSaveError(null);
    try {
      if (wasSaved) await patientApi.removeFavorite(providerId);
      else await patientApi.addFavorite(providerId);
    } catch {
      // A save the server refused is not a save.
      setSaved(wasSaved);
      setSaveError('Could not update your saved doctors. Please try again.');
    }
  };

  if (loading) return <Spinner />;
  if (error || !doctor) {
    return (
      <div style={{ background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: T.font, paddingBottom: 100 }}>
        <div style={{ textAlign: 'center', padding: 24 }}>
          <p style={{ color: T.textSec, fontSize: 14 }}>{error || 'Doctor not found.'}</p>
          <button onClick={() => navigate(-1)} style={{ background: T.teal, color: T.white, border: 'none', borderRadius: 10, padding: '10px 20px', fontFamily: T.font, fontWeight: 700, cursor: 'pointer', marginTop: 12 }}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const pricing: ProviderPricing[] = (() => {
    if (!doctor.pricing) return [];
    const p = typeof doctor.pricing === 'string' ? JSON.parse(doctor.pricing) : doctor.pricing;
    return Array.isArray(p) ? p : [];
  })();

  const certificates: ProviderCertificate[] = (() => {
    if (!doctor.certificates) return [];
    const c = typeof doctor.certificates === 'string' ? JSON.parse(doctor.certificates) : doctor.certificates;
    return Array.isArray(c) ? c : [];
  })();

  // The doctor's own published record. Null when this listing has no doctor
  // profile behind it — every block below then simply does not draw.
  const dp = doctor.doctor ?? null;
  // `photoUrl` is a path on the API host. Rendered as-is it works behind the
  // dev proxy and, in production, is answered by this site's index.html with a
  // 200 — so the photo silently fell back to the initials for every doctor.
  const photoUrl = apiFileUrl(dp?.photoUrl ?? doctor.logoUrl);
  const modes = dp?.consultation.modes ?? doctor.consultModes ?? [];
  const offersClinic = modes.length === 0 || modes.includes('IN_CLINIC');
  const offersVideo = modes.length === 0 || modes.includes('ONLINE');

  const firstClinic = dp?.clinics?.[0];
  const practiceLine =
    [doctor.city, doctor.state].filter(Boolean).join(', ')
    || [firstClinic?.name, firstClinic?.city].filter(Boolean).join(', ')
    || 'Practice not stated';

  const certTypeBadgeColor: Record<string, string> = {
    license: T.teal,
    accreditation: T.navy,
    certification: '#7C3AED',
    registration: '#059669',
  };

  return (
    <div
      style={{
        background: T.bg,
        fontFamily: T.font,
        margin: '0 auto',
        paddingBottom: 100,
      }}
    >
      {/* ── Hero Card ─────────────────────────────────────────── */}
      <div
        style={{
          background: `linear-gradient(160deg, ${T.navy} 0%, #003670 60%, ${T.teal}80 100%)`,
          padding: '28px 20px 32px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative circle */}
        <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', position: 'relative' }}>
          {/* Avatar — the doctor's own photo when they have uploaded one. It
              was mapped and never rendered, so every doctor wore the same
              stethoscope. */}
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: 18,
              background: 'rgba(255,255,255,0.12)',
              border: '2px solid rgba(255,255,255,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {photoUrl && !photoBroken
              ? <img src={photoUrl} alt="" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setPhotoBroken(true)} />
              : <IconStethoscope color="rgba(255,255,255,0.9)" />}
          </div>

          {/* Doctor info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h1 style={{ fontFamily: T.font, fontSize: 18, fontWeight: 800, color: T.white, margin: 0 }}>
                {doctor.name}
              </h1>
              {/* Verification is a two-state fact, and the pending state is
                  the one a patient needs. It used to render nothing at all. */}
              {doctor.isVerified ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    background: T.teal + '25',
                    border: `1px solid ${T.teal}50`,
                    borderRadius: 20,
                    padding: '3px 8px',
                  }}
                >
                  <IconVerified color={T.teal} />
                  <span style={{ fontFamily: T.font, fontSize: 9, fontWeight: 700, color: T.teal, letterSpacing: '0.06em' }}>VERIFIED</span>
                </span>
              ) : (
                <span
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'rgba(245,158,11,0.22)', border: '1px solid rgba(245,158,11,0.5)',
                    borderRadius: 20, padding: '3px 8px',
                  }}
                >
                  <span style={{ fontFamily: T.font, fontSize: 9, fontWeight: 700, color: '#FDE68A', letterSpacing: '0.06em' }}>
                    VERIFICATION PENDING
                  </span>
                </span>
              )}
            </div>

            {doctor.specialties?.length > 0 && (
              <div style={{ fontFamily: T.font, fontSize: 13, color: 'rgba(255,255,255,0.8)', marginTop: 4 }}>
                {doctor.specialties.join(' • ')}
              </div>
            )}

            {/* Rating. An unrated doctor said nothing at all here, which reads
                as an omission; every doctor on the network is currently
                unrated, so every profile was missing this line. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
              {(doctor.rating ?? 0) > 0 ? (
                <>
                  <span style={{ display: 'inline-flex', gap: 2 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <IconStar key={i} filled={i <= Math.round(doctor.rating)} color="#F59E0B" />
                    ))}
                  </span>
                  <span style={{ fontFamily: T.font, fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>
                    {doctor.rating?.toFixed(1)} ({doctor.reviewCount} reviews)
                  </span>
                </>
              ) : (
                <span style={{
                  fontFamily: T.font, fontSize: 10.5, fontWeight: 700,
                  color: 'rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.22)', borderRadius: 20, padding: '3px 10px',
                }}>
                  New · not yet rated
                </span>
              )}
            </div>

            {/* Where they practise: the directory row first, then the clinic
                they registered — several doctors have a clinic on file and no
                city on the listing, and the profile said nothing at all.
                "Practice not stated" only when neither records one. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6 }}>
              <IconLocation color="rgba(255,255,255,0.55)" />
              <span style={{ fontFamily: T.font, fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                {practiceLine}
              </span>
            </div>

            {/* How this doctor consults, as the server derived it from the
                schedules they published — the same two tags the app's profile
                carries. An empty list means nothing is known, so nothing is
                claimed here and the services card says so. */}
            {modes.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {modes.includes('IN_CLINIC') && <span style={heroChip}>Clinic</span>}
                {modes.includes('ONLINE') && <span style={heroChip}>Telemedicine</span>}
              </div>
            )}

            {/* Experience and languages — the doctor's own record. */}
            {(dp?.experienceYears || (dp?.languages.length ?? 0) > 0) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {dp?.experienceYears ? (
                  <span style={heroChip}>{dp.experienceYears} years experience</span>
                ) : null}
                {(dp?.languages ?? []).slice(0, 3).map((l) => (
                  <span key={l} style={heroChip}>{l}</span>
                ))}
              </div>
            )}

            {/* The doctor's NeoHealthCard id, when one has been issued. */}
            {dp?.nhcDoctorId && (
              <div style={{ fontFamily: T.mono, fontSize: 10.5, color: 'rgba(255,255,255,0.65)', marginTop: 8, letterSpacing: '0.04em' }}>
                NHC · {dp.nhcDoctorId}
              </div>
            )}
          </div>

          {/* Save this doctor — writes to the patient's real favourites, the
              same list the directory's hearts read. */}
          <button
            onClick={() => void toggleSaved()}
            aria-label={saved ? 'Remove from saved doctors' : 'Save this doctor'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', flexShrink: 0, alignSelf: 'flex-start' }}
          >
            <svg width={24} height={24} viewBox="0 0 24 24" fill={saved ? '#F87171' : 'none'} stroke={saved ? '#F87171' : 'rgba(255,255,255,0.7)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21.2l7.7-7.7 1.1-1.1a5.5 5.5 0 0 0 0-7.8z" />
            </svg>
          </button>
        </div>

        {saveError && (
          <div style={{ fontFamily: T.font, fontSize: 11, color: '#FDE68A', marginTop: 10, position: 'relative' }}>{saveError}</div>
        )}

        {/* Description */}
        {doctor.description && (
          <p
            style={{
              fontFamily: T.font,
              fontSize: 13,
              color: 'rgba(255,255,255,0.7)',
              margin: '16px 0 0',
              lineHeight: 1.7,
              position: 'relative',
            }}
          >
            {doctor.description}
          </p>
        )}
      </div>

      {/* ── Medical registration & compliance ─────────────────── */}
      {dp && <RegistrationCard doctor={dp} providerVerified={doctor.isVerified} />}

      {/* ── Services offered, from what the doctor publishes ───── */}
      {dp && (
        <ServicesCard
          doctor={dp}
          doctorName={doctor.name}
          onBook={(mode) => {
            const params = new URLSearchParams({ type: mode === 'ONLINE' ? 'telemedicine' : 'CLINIC' });
            if (selectedSlot) {
              params.set('date', getDateStr(selectedDate));
              params.set('time', selectedSlot);
              if (selectedSlotId) params.set('slotId', selectedSlotId);
            }
            navigate(`/patient/care-services/doctors/${doctorId}/book?${params.toString()}`);
          }}
        />
      )}

      {/* ── About, training, languages ─────────────────────────── */}
      {dp && <AboutCard doctor={dp} />}

      {/* ── Services / Pricing ────────────────────────────────── */}
      {pricing.length > 0 && (
        <>
          <SectionHeader title="Consultation Fees" />
          <div style={{ padding: '0 16px' }}>
            {pricing.map((p, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 12,
                  padding: '13px 16px',
                  marginBottom: 8,
                }}
              >
                <div>
                  <div style={{ fontFamily: T.font, fontSize: 13, fontWeight: 600, color: T.text }}>{p.service}</div>
                  {p.note && <div style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, marginTop: 2 }}>{p.note}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontFamily: T.font, fontSize: 16, fontWeight: 800, color: T.navy }}>₹{p.price}</span>
                  {p.unit && <span style={{ fontFamily: T.font, fontSize: 10, color: T.textTer }}>/{p.unit}</span>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Schedule ──────────────────────────────────────────── */}
      <SectionHeader title="Preferred Time (clinic confirms your slot)" />
      <div style={{ padding: '0 16px' }}>
        {/* Date chips */}
        <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
          <div style={{ display: 'flex', gap: 8, width: 'max-content' }}>
            {[0, 1, 2, 3, 4].map((offset) => (
              <button
                key={offset}
                onClick={() => { setSelectedDate(offset); setSelectedSlot(null); }}
                style={{
                  fontFamily: T.font,
                  fontSize: 12,
                  fontWeight: 600,
                  color: selectedDate === offset ? T.white : T.textSec,
                  background: selectedDate === offset ? T.teal : T.white,
                  border: `1.5px solid ${selectedDate === offset ? T.teal : T.border}`,
                  borderRadius: 12,
                  padding: '8px 14px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s ease',
                  boxShadow: selectedDate === offset ? `0 2px 8px ${T.teal}35` : T.shadow,
                }}
              >
                {getDateLabel(offset)}
              </button>
            ))}
          </div>
        </div>

        {/* Time chips — the doctor's published openings when there are any,
            otherwise a preference the clinic confirms. */}
        {slots.loading ? (
          <div style={{ marginTop: 12, fontFamily: T.font, fontSize: 12, color: T.textSec }}>Checking availability…</div>
        ) : slots.hasSchedule ? (
          (() => {
            const open = slots.slotsFor(getDateStr(selectedDate));
            if (open.length === 0) {
              return (
                <div style={{
                  marginTop: 12, fontFamily: T.font, fontSize: 12, color: T.textSec,
                  background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 12px',
                }}>
                  No openings left on this date.
                  {slots.openDates.length > 0
                    ? ` Try ${slots.openDates.slice(0, 3).map((d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })).join(', ')}.`
                    : ''}
                </div>
              );
            }
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                {open.map((slot) => {
                  const isSelected = selectedSlotId === slot.id;
                  return (
                    <button
                      key={slot.id}
                      onClick={() => { setSelectedSlot(slot.startTime); setSelectedSlotId(slot.id); }}
                      style={{
                        fontFamily: T.font, fontSize: 12, fontWeight: 600,
                        color: isSelected ? T.white : T.navy,
                        background: isSelected ? T.teal : T.white,
                        border: `1.5px solid ${isSelected ? T.teal : T.navy + '30'}`,
                        borderRadius: 10, padding: '8px 14px', cursor: 'pointer',
                        transition: 'all 0.12s ease',
                        boxShadow: isSelected ? `0 2px 8px ${T.teal}35` : 'none',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                      }}
                    >
                      <span>{formatSlotTime(slot.startTime)}</span>
                      <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.75 }}>
                        {slot.remaining === 1 ? 'last seat' : `${slot.remaining} seats`}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })()
        ) : (
          <>
            <div style={{
              marginTop: 12, fontFamily: T.font, fontSize: 11.5, color: T.textSec, lineHeight: 1.5,
              background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 12px',
            }}>
              This doctor does not publish a schedule here — pick a preferred time and the clinic confirms it.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              {preferredTimeGrid().map((time) => {
                const isSelected = selectedSlot === time && !selectedSlotId;
                return (
                  <button
                    key={time}
                    onClick={() => { setSelectedSlot(time); setSelectedSlotId(null); }}
                    style={{
                      fontFamily: T.font, fontSize: 12, fontWeight: 600,
                      color: isSelected ? T.white : T.navy,
                      background: isSelected ? T.teal : T.white,
                      border: `1.5px solid ${isSelected ? T.teal : T.navy + '30'}`,
                      borderRadius: 10, padding: '8px 14px', cursor: 'pointer',
                      transition: 'all 0.12s ease',
                      boxShadow: isSelected ? `0 2px 8px ${T.teal}35` : 'none',
                    }}
                  >
                    {formatSlotTime(time)}
                  </button>
                );
              })}
            </div>
          </>
        )}
        {selectedSlot && (
          <div style={{ marginTop: 10, fontFamily: T.font, fontSize: 12, color: T.teal, fontWeight: 600 }}>
            {selectedSlotId ? 'Selected' : 'Preferred'}: {getDateLabel(selectedDate)}, {formatSlotTime(selectedSlot)}
            {selectedSlotId ? '' : ' · confirmed by the clinic'}
          </div>
        )}
      </div>

      {/* ── Where this doctor sits ────────────────────────────── */}
      {dp && dp.clinics.length > 0 && <ClinicsCard doctor={dp} />}

      {/* ── Location & Contact ─────────────────────────────────—
          Only for a listing with no clinic on file. A doctor's own phone and
          e-mail are NOT published: NeoHealthCard does not hand out a doctor's
          personal line, and the clinic's number is on the clinic card above. */}
      {(!dp || dp.clinics.length === 0) && (doctor.address || doctor.openHours) && (
        <>
          <SectionHeader title="Location & Contact" />
          <div style={{ padding: '0 16px' }}>
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {doctor.address && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: T.navy + '0D', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <IconLocation color={T.navy} />
                  </div>
                  <div>
                    <div style={{ fontFamily: T.font, fontSize: 13, color: T.text }}>{doctor.address}</div>
                    {(doctor.city || doctor.state) && (
                      <div style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, marginTop: 2 }}>
                        {[doctor.city, doctor.state].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {doctor.openHours && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: T.teal + '0D', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <IconClock color={T.teal} />
                  </div>
                  <div style={{ fontFamily: T.font, fontSize: 13, color: T.text }}>{doctor.openHours}</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Certificates ──────────────────────────────────────── */}
      {certificates.length > 0 && (
        <>
          <SectionHeader title="Qualifications & Certificates" />
          <div style={{ padding: '0 16px' }}>
            {certificates.map((cert, i) => (
              <div
                key={i}
                style={{
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 12,
                  padding: '14px 16px',
                  marginBottom: 8,
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ width: 32, height: 32, borderRadius: 9, background: (certTypeBadgeColor[cert.type] || T.navy) + '12', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <IconCertificate color={certTypeBadgeColor[cert.type] || T.navy} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text }}>{cert.name}</span>
                    <span
                      style={{
                        fontFamily: T.font,
                        fontSize: 9,
                        fontWeight: 700,
                        color: certTypeBadgeColor[cert.type] || T.navy,
                        background: (certTypeBadgeColor[cert.type] || T.navy) + '12',
                        border: `1px solid ${(certTypeBadgeColor[cert.type] || T.navy)}25`,
                        borderRadius: 20,
                        padding: '2px 8px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {cert.type}
                    </span>
                  </div>
                  <div style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, marginTop: 3 }}>
                    {cert.issuedBy}
                    {cert.year ? ` · ${cert.year}` : ''}
                  </div>
                  {cert.registrationNumber && (
                    <div style={{ fontFamily: T.font, fontSize: 11, color: T.textTer, marginTop: 2 }}>
                      Reg. #{cert.registrationNumber}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Accreditations ───────────────────────────────────── */}
      <AccreditationsCard rows={doctor.accreditations ?? []} />

      {/* ── Tags ─────────────────────────────────────────────── */}
      {doctor.tags?.length > 0 && (
        <>
          <SectionHeader title="Specializations" />
          <div style={{ padding: '0 16px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {doctor.tags.map((tag) => (
              <span
                key={tag}
                style={{
                  fontFamily: T.font,
                  fontSize: 11,
                  fontWeight: 600,
                  color: T.navy,
                  background: T.navy + '08',
                  border: `1px solid ${T.navy}18`,
                  borderRadius: 20,
                  padding: '6px 13px',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </>
      )}

      {/* ── Patient Reviews ──────────────────────────────────── */}
      {/* The per-review feed does exist: `/providers/:idOrSlug/reviews` has
          been serving it all along. This page used to say reviews were
          "coming soon" over an average nobody could see the basis of. */}
      <SectionHeader title="Patient Reviews" />
      <div style={{ padding: '0 16px' }}>
        <ProviderReviews providerKey={doctor.slug || doctor.id} />
      </div>

      {/* ── Safety Note ───────────────────────────────────────── */}
      <div style={{ padding: '20px 16px 0' }}>
        <div
          style={{
            background: T.teal + '08',
            border: `1px solid ${T.teal}25`,
            borderRadius: 12,
            padding: '13px 16px',
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <IconShield color={T.teal} />
          <p style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, margin: 0, lineHeight: 1.7 }}>
            <strong style={{ color: T.teal }}>{doctor.isVerified ? 'NeoHealthCard™ Verified Provider' : 'Listed on NeoHealthCard™'}</strong> · Records shared only with your consent.
          </p>
        </div>
      </div>

      {/* ── Frequently asked questions ────────────────────────── */}
      {dp && <FaqCard doctor={dp} doctorName={doctor.name} />}

      {/* ── Who provides the care ─────────────────────────────── */}
      <ProvidedByFooter provider={doctor} />

      {/* ── Share Profile ──────────────────────────────── */}
      <div style={{ padding: '10px 16px 0', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={() => {
            const ratingLine = (doctor.rating ?? 0) > 0 ? `\nRating: ${doctor.rating.toFixed(1)} (${doctor.reviewCount} reviews)` : '';
            const text = `${doctor.name}\n${doctor.specialties?.join(', ')}\n${doctor.city || ''}${ratingLine}\n\nView on NeoHealthCard`;
            if (navigator.share) { navigator.share({ title: doctor.name, text }).catch(() => {}); }
            else { navigator.clipboard.writeText(text).catch(() => {}); }
          }}
          style={{
            fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.teal,
            background: T.teal + '10', border: `1px solid ${T.teal}25`,
            borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5,
          }}
        >
          <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" /></svg>
          Share Profile
        </button>
      </div>

      {/* ── Sticky Action Buttons ──────────────────────────────── */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: T.white,
          borderTop: `1px solid ${T.border}`,
          padding: '14px 16px 20px',
          display: 'flex',
          gap: 10,
          zIndex: 30,
          boxShadow: '0 -4px 16px rgba(11,37,69,0.08)',
        }}
      >
        {offersClinic && <button
          onClick={() => {
            const params = new URLSearchParams({ type: 'CLINIC' });
            if (selectedSlot) {
              params.set('date', getDateStr(selectedDate));
              params.set('time', selectedSlot);
              // A published slot keeps its id so the booking screen can take
              // that exact seat instead of re-asking.
              if (selectedSlotId) params.set('slotId', selectedSlotId);
            }
            navigate(`/patient/care-services/doctors/${doctorId}/book?${params.toString()}`);
          }}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 7,
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 700,
            color: T.white,
            background: `linear-gradient(135deg, ${T.navy} 0%, #003670 100%)`,
            border: 'none',
            borderRadius: 12,
            padding: '13px 0',
            cursor: 'pointer',
            boxShadow: `0 3px 10px ${T.navy}30`,
          }}
        >
          <IconBuilding color={T.white} />
          Book Clinic Visit
        </button>}
        {/* A doctor who publishes only clinic consultations is not offered as
            a video consultation. Both buttons used to show for everyone. */}
        {offersVideo && <button
          onClick={() => {
            const params = new URLSearchParams({ type: 'telemedicine' });
            if (selectedSlot) {
              params.set('date', getDateStr(selectedDate));
              params.set('time', selectedSlot);
            }
            navigate(`/patient/care-services/doctors/${doctorId}/book?${params.toString()}`);
          }}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 7,
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 700,
            color: T.teal,
            background: T.white,
            border: `2px solid ${T.teal}`,
            borderRadius: 12,
            padding: '13px 0',
            cursor: 'pointer',
          }}
        >
          <IconVideo color={T.teal} />
          Telemedicine
        </button>}
      </div>
    </div>
  );
}
